import React, { useMemo } from 'react';
import { MoreVertical } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import PopMenu from 'features/playground/components/PopMenu';
import ProviderLogo from 'shared/components/ui/ProviderLogo';
import QuestionsEditor from 'shared/decision/QuestionsEditor';
import { fromApi } from 'shared/decision/questions';
import { DECISION_PRESETS, presetStateText } from 'features/playground/lib/decisionPresets';
import {
  activityToolbarButtonClass, primaryButtonClass, drawerIconButtonClass, sectionTitleClass, menuItemClass, dataTableFocusRing,
} from 'features/playground/styles';

/**
 * DecisionPanel — a Decision task on the bench: a state, typed questions, and
 * a decision model (Jev, Trodo) that answers them with probabilities. Same
 * layout as the Console playground: State on top, Questions below, model +
 * "Allow can't tell" + Run in the footer. Answers render in the Results
 * section under the task (OutputColumn).
 *
 * Controlled: everything lives in `panel` (parent-owned):
 *   { mode: 'decision', title, state, questions: editor rows,
 *     decision: { integration_id, provider, model }, allow_abstain, result }
 */

/** The integration + model this panel runs with: its own pick, else the first decision model. */
export function effectiveDecisionModel(panel, integrations, catalog) {
  const d = panel.decision || {};
  const integ = (integrations || []).find((x) => String(x.id) === String(d.integration_id)) || (integrations || [])[0] || null;
  if (!integ) return { integ: null, model: '' };
  const prov = (catalog || []).find((p) => p.provider === integ.provider);
  const sameInteg = String(d.integration_id) === String(integ.id);
  const model = (sameInteg && d.model) || integ.default_model || prov?.default_model || '';
  return { integ, model, provider: prov };
}

/** The playground's h-8 field look on the shared brand dropdown. */
const dropdownTriggerClass = `!h-8 !rounded-md !px-2.5 !text-[12px] hover:!border-[color:var(--border-strong)] ${dataTableFocusRing}`;

const textareaClass = 'min-h-[180px] w-full resize-y rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 py-2 font-mono text-[12px] leading-5 text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] transition-[border-color] duration-150 focus:border-[color:var(--border-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

export default function DecisionPanel({
  panel, index, onChange, onRemove, onDuplicate, onRun, canRemove, running,
  integrations, catalog, loadingModels, settingsHref, onOpenSettings, readOnly = false, showProblems = false,
}) {
  const rows = panel.questions || [];
  const { integ, model, provider } = useMemo(
    () => effectiveDecisionModel(panel, integrations, catalog),
    [panel, integrations, catalog],
  );
  const models = provider?.models || (model ? [{ id: model, label: model }] : []);
  const comingSoon = provider && provider.live === false;

  const patch = (p) => onChange(p);
  const loadPreset = (preset) => patch({
    state: presetStateText(preset),
    questions: fromApi(preset.questions),
    title: panel.title || preset.label,
  });

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !readOnly && !running && integ) {
      e.preventDefault();
      onRun();
    }
  };

  return (
    <section className="flex h-full min-w-[360px] flex-1 flex-col overflow-hidden" onKeyDown={onKeyDown} data-testid="decision-panel">
      <div className="flex h-12 shrink-0 items-center gap-1 px-4">
        <input
          value={panel.title || ''}
          onChange={(e) => patch({ title: e.target.value })}
          placeholder={index === 0 ? 'Base task' : 'Decision task'}
          aria-label="Task title"
          readOnly={readOnly}
          className="-ml-1.5 h-8 min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1.5 text-[13px] font-semibold text-[color:var(--text-primary)] placeholder:font-normal placeholder:text-[color:var(--text-disabled)] hover:border-[color:var(--border-default)] focus:border-[color:var(--border-strong)] focus:outline-none"
        />
        <span className="shrink-0 rounded border border-[color:var(--border-default)] px-1 text-[10px] leading-4 text-[color:var(--text-muted)]">Decision</span>
        {!readOnly && (
          <PopMenu
            label="Task options"
            width={176}
            renderTrigger={({ ref, onClick, ariaProps }) => (
              <button ref={ref} type="button" onClick={onClick} title="Task options" aria-label="Task options"
                className={drawerIconButtonClass} {...ariaProps}>
                <MoreVertical size={15} />
              </button>
            )}
          >
            {(close) => (
              <>
                <button type="button" role="menuitem" onClick={() => { close(); onDuplicate(); }} className={menuItemClass}>
                  Duplicate
                </button>
                {canRemove && (
                  <button type="button" role="menuitem" onClick={() => { close(); onRemove(); }}
                    className={`${menuItemClass} text-[color:var(--feedback-error)]`}>
                    Remove
                  </button>
                )}
              </>
            )}
          </PopMenu>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4">
        <div className="flex flex-col gap-2">
          <h3 className={sectionTitleClass}>
            State
            {!readOnly && (
              <PopMenu
                label="Examples"
                width={200}
                renderTrigger={({ ref, onClick, ariaProps }) => (
                  <button ref={ref} type="button" onClick={onClick} className={`${activityToolbarButtonClass} !h-7 font-normal`} {...ariaProps}>
                    Examples
                  </button>
                )}
              >
                {(close) => DECISION_PRESETS.map((p) => (
                  <button key={p.id} type="button" role="menuitem" className={menuItemClass}
                    onClick={() => { close(); loadPreset(p); }}>
                    {p.label}
                  </button>
                ))}
              </PopMenu>
            )}
          </h3>
          <textarea
            className={textareaClass}
            value={panel.state || ''}
            readOnly={readOnly}
            spellCheck={false}
            aria-label="State"
            placeholder="Paste state"
            onChange={(e) => patch({ state: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-2">
          <h3 className={sectionTitleClass}>
            <span>Questions <span className="font-normal text-[color:var(--text-muted)]">{rows.length}</span></span>
          </h3>
          <QuestionsEditor
            rows={rows}
            onChange={(next) => patch({ questions: next })}
            readOnly={readOnly}
            showProblems={showProblems}
          />
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-[color:var(--border-subtle)] px-4 py-2">
        {!integrations?.length ? (
          <p className="flex-1 text-[12px] text-[color:var(--text-muted)]">
            {loadingModels ? 'Loading' : (
              <>
                Add a Jev or Hartley key in{' '}
                <a
                  href={settingsHref}
                  onClick={(e) => { if (onOpenSettings) { e.preventDefault(); onOpenSettings(); } }}
                  className="text-[color:var(--text-primary)] underline underline-offset-2"
                >
                  Settings → Models
                </a>
              </>
            )}
          </p>
        ) : (
          <>
            <div className="min-w-[120px] flex-1">
              <BrandDropdown
                value={integ ? String(integ.id) : ''}
                onChange={(id) => {
                  const next = integrations.find((x) => String(x.id) === String(id));
                  patch({ decision: { integration_id: next?.id || '', provider: next?.provider || '', model: next?.default_model || '' } });
                }}
                ariaLabel="Decision model"
                placeholder="Provider"
                disabled={readOnly}
                triggerClassName={dropdownTriggerClass}
                menuClassName="!rounded-lg"
                options={integrations.map((x) => ({
                  value: String(x.id), label: x.label, icon: <ProviderLogo provider={x.provider} size={16} />,
                }))}
              />
            </div>
            <div className="min-w-[120px] flex-1">
              <BrandDropdown
                value={model}
                onChange={(m) => patch({ decision: { integration_id: integ.id, provider: integ.provider, model: m } })}
                ariaLabel="Model"
                placeholder="Model"
                disabled={readOnly}
                triggerClassName={dropdownTriggerClass}
                menuClassName="!rounded-lg"
                options={models.map((m) => ({
                  value: m.id, label: `${m.label || m.id}${comingSoon ? ' · coming soon' : ''}`,
                }))}
              />
            </div>
          </>
        )}
        <label className="flex shrink-0 items-center gap-1.5 text-[12px] text-[color:var(--text-primary)]">
          <input
            type="checkbox"
            checked={panel.allow_abstain !== false}
            disabled={readOnly}
            onChange={(e) => patch({ allow_abstain: e.target.checked })}
          />
          Allow can't tell
        </label>
        {!readOnly && (
          <>
            <button type="button" onClick={onRun} disabled={running || !integ} className={primaryButtonClass}>
              {running ? 'Running' : 'Run'}
            </button>
          </>
        )}
      </div>
    </section>
  );
}
